import { ApiClient } from "../../../api/ApiClient";

const api = ApiClient.getDefault();

export interface SliceItem {
  key: string;
  label: string;
  count: number;
  percent: number;
}

export interface QualificationsDto {
  job: SliceItem[]; income: SliceItem[]; house: SliceItem[];
  vehicle: SliceItem[]; socialSecurity: SliceItem[]; providentFund: SliceItem[];
  credit: SliceItem[]; loanType: SliceItem[];
}

export interface AssetsDto {
  total: number;
  byType: SliceItem[]; byStatus: SliceItem[]; byStar: SliceItem[];
  qualifications: QualificationsDto;
}

export interface CallEffectivenessDto {
  totalCalls: number; effectiveCalls: number; ineffectiveCalls: number;
  effectiveRate: number; newAssigned: number; oldAssigned: number;
}

export interface DailyTrendItem {
  date: string; newAssigned: number; oldAssigned: number;
  called: number; effective: number;
}

export interface NewStarDistDto {
  starDist: SliceItem[]; daily: DailyTrendItem[];
}

export interface HeatmapCell {
  dow?: string; date?: string; hour: number; count: number;
}

export interface HeatmapDto { mode: string; cells: HeatmapCell[]; }

export interface StarDistDto {
  total: number; starDist: SliceItem[]; avgStar: number;
}

export interface MonthlyEntryItem { month: string; count: number; }

export interface FunnelDetailDto {
  month: string; entryMode: string;
  total: number; assigned: number; effectiveCall: number;
  visited: number; signed: number; paid: number;
}

export interface LeaderboardItem {
  rank: number; saleId: number; saleName: string; teamName: string; count: number;
}

export interface DailyQualityTrendItem {
  date: string; excellent: number; good: number; normal: number; poor: number; noScore: number;
}

export interface QualityDistDto {
  dist: SliceItem[];
  daily: DailyQualityTrendItem[];
}

export interface DailyIntentionTrendItem {
  date: string; a: number; b: number; c: number; d: number; e: number; pending: number;
}

export interface IntentionDistDto {
  dist: SliceItem[];
  daily: DailyIntentionTrendItem[];
}

export interface DashboardPref {
  id?: number; userId?: number;
  moduleCode: string; visible: boolean; autoExpand: boolean; sortOrder: number;
}

export interface DashboardFilters {
  startDate: string;
  endDate: string;
  teamId?: number;
  saleIds?: number[];
}

// Helper: unwrap ApiResp<T> → T (or throw on biz error)
async function unwrap<T>(p: Promise<{ code: string; msg: string; data: T }>): Promise<T> {
  const r = await p;
  return r.data;
}

export const HomeDashboardApi = {
  getPrefs: () => unwrap(api.request<DashboardPref[]>("/homeDashboard/pref", "GET")),
  updatePrefs: (prefs: DashboardPref[]) =>
    unwrap(api.request<DashboardPref[]>("/homeDashboard/pref", "PUT", undefined, prefs as any)),

  getAssets: (f: DashboardFilters) =>
    unwrap(api.request<AssetsDto>("/homeDashboard/assets", "GET", f as any)),

  getCallEffectiveness: (f: DashboardFilters) =>
    unwrap(api.request<CallEffectivenessDto>("/homeDashboard/call/effectiveness", "GET", f as any)),
  getCallDailyTrend: (f: DashboardFilters) =>
    unwrap(api.request<DailyTrendItem[]>("/homeDashboard/call/dailyTrend", "GET", f as any)),
  getCallNewStarDist: (f: DashboardFilters) =>
    unwrap(api.request<NewStarDistDto>("/homeDashboard/call/newStarDist", "GET", f as any)),
  getCallHeatmap: (mode: "week" | "month", teamId?: number, saleIds?: number[]) =>
    unwrap(api.request<HeatmapDto>("/homeDashboard/call/heatmap", "GET", { mode, teamId, saleIds })),

  getVisitStarDist: (f: DashboardFilters) =>
    unwrap(api.request<StarDistDto>("/homeDashboard/visit/starDist", "GET", f as any)),
  getVisitDailyTrend: (f: DashboardFilters) =>
    unwrap(api.request<DailyTrendItem[]>("/homeDashboard/visit/dailyTrend", "GET", f as any)),

  getFunnelMonthlyEntries: (entryMode: "created" | "assigned", teamId?: number, saleIds?: number[]) =>
    unwrap(api.request<MonthlyEntryItem[]>("/homeDashboard/funnel/monthlyEntries", "GET", { entryMode, teamId, saleIds })),
  getFunnelMonthDetail: (month: string, entryMode: "created" | "assigned", teamId?: number, saleIds?: number[]) =>
    unwrap(api.request<FunnelDetailDto>("/homeDashboard/funnel/monthDetail", "GET", { month, entryMode, teamId, saleIds })),

  getLeaderboardNew: (f: DashboardFilters, limit = 10) =>
    unwrap(api.request<LeaderboardItem[]>("/homeDashboard/leaderboard/new", "GET", { ...f, limit })),
  getLeaderboardVisit: (f: DashboardFilters, limit = 10) =>
    unwrap(api.request<LeaderboardItem[]>("/homeDashboard/leaderboard/visit", "GET", { ...f, limit })),

  getAiQualityDist: (f: DashboardFilters) =>
    unwrap(api.request<QualityDistDto>("/homeDashboard/ai/qualityDist", "GET", f as any)),
  getAiIntentionDist: (f: DashboardFilters) =>
    unwrap(api.request<IntentionDistDto>("/homeDashboard/ai/intentionDist", "GET", f as any)),
};
